import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { toast } from "sonner";
import { ImageSquare, FileXls, MagnifyingGlassMinus, Table } from "@phosphor-icons/react";
import api from "../../../lib/api";
import { SERIES, LOAN_COLORS, LOAN_DASHES, chartValues, tableColumns } from "../../../lib/chartSeries";
import { LoanChart } from "./LoanChart";
import { DonutChart } from "./DonutChart";
import { ChartLegend } from "./ChartLegend";
import { ChartDataTable } from "./ChartDataTable";
import { RangeSlider } from "./RangeSlider";
import { chartToPng, downloadHref, downloadBlob } from "./chartExport";

const DONUT_COLS = [
  { key: "label", label: "Segment", fmt: "text" },
  { key: "value", label: "Amount" },
  { key: "pct", label: "Share", fmt: "pct" },
];
const BAR_TYPES = ["stacked", "grouped", "combo"];

function themeColors() {
  const v = (n) => { try { return getComputedStyle(document.documentElement).getPropertyValue(n).trim(); } catch { return ""; } };
  const h = (n, fb) => (v(n) ? `hsl(${v(n)})` : fb);
  return { fg: h("--muted-foreground", "#777"), grid: h("--border", "#ccc"), accent: h("--secondary", "#B89B72"),
    bg: h("--card", "#fff"), text: h("--foreground", "#222") };
}

function drawnSeries(s, mode, loanNames) {
  if (mode === "split") {
    return loanNames.map((n, j) => ({ key: `L${j}`, label: n, color: LOAN_COLORS[j % 8], dash: LOAN_DASHES[j % 8], hatch: j % 2 === 1 }));
  }
  const pct = s.display === "pct";
  return SERIES
    .filter((x) => (s.series[x.key] || (s.chartType === "combo" && x.key === "balance")) && (!pct || ["principal", "interest"].includes(x.key)))
    .map((x) => ({ ...x, hatch: x.key === "interest" }));
}

export function CustomChartCard({ built, donutRows, s, mode, title, summary, reportRef, theme }) {
  const box = useRef(null);
  const [hidden, setHidden] = useState(() => new Set());
  const [zoom, setZoom] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [include, setInclude] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const colors = useMemo(themeColors, [theme]);
  const donut = s.chartType === "donut";
  const values = useMemo(() => chartValues(built.rows, s, built.loans.length), [built, s]);
  useEffect(() => { setZoom(null); }, [values.length, s.timeAxis, s.window]);
  const visible = zoom ? values.slice(zoom[0], zoom[1] + 1) : values;
  const drawn = useMemo(() => drawnSeries(s, mode, built.loans), [s, mode, built.loans]);
  const columns = useMemo(() => (donut ? DONUT_COLS : tableColumns(mode, built.loans)), [donut, mode, built.loans]);
  const tableRows = donut ? donutRows : built.rows;
  const legend = useMemo(() => (donut
    ? donutRows.map((d) => ({ key: d.key, label: d.label, color: d.color, bar: true, hatch: d.key.startsWith("i") }))
    : drawn.map((d) => ({ ...d, bar: BAR_TYPES.includes(s.chartType) && !["balance", "emi"].includes(d.key) }))),
  [donut, donutRows, drawn, s.chartType]);
  const uid = `lc${mode}${s.chartType}`;

  const toggle = useCallback((k) => setHidden((h) => { const n = new Set(h); if (n.has(k)) n.delete(k); else n.add(k); return n; }), []);
  const toPng = useCallback(() => chartToPng(box.current, {
    title, legend: legend.filter((l) => !hidden.has(l.key)), bg: colors.bg, fg: colors.text,
  }), [title, legend, hidden, colors]);

  useEffect(() => {
    if (!reportRef) return undefined;
    reportRef.current = include
      ? { build: async () => ({ title, summary, image_png: await toPng(), columns, rows: tableRows }) }
      : null;
    return () => { reportRef.current = null; };
  }, [reportRef, include, toPng, title, summary, columns, tableRows]);

  const onZoom = (la, lb) => {
    const ia = values.findIndex((r) => r.label === la);
    const ib = values.findIndex((r) => r.label === lb);
    if (ia >= 0 && ib >= 0) setZoom([Math.min(ia, ib), Math.max(ia, ib)]);
  };
  const downloadPng = async () => {
    try {
      const url = await toPng();
      if (url) downloadHref(url, "Estima_Loan_Chart.png");
    } catch {
      toast.error("Could not create the image. Please try again.");
    }
  };
  const downloadXlsx = async () => {
    try {
      const r = await api.post("/loans/chart-data/xlsx", { title, summary, columns, rows: tableRows }, { responseType: "blob" });
      downloadBlob(r.data, "Estima_Loan_Chart_Data.xlsx");
    } catch {
      toast.error("Could not create the Excel file. Please try again.");
    }
  };

  const empty = donut ? !donutRows?.some((d) => d.value > 0) : !values.length;
  return (
    <div className="card-flat p-4 space-y-3" data-testid="custom-chart-card">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 w-full sm:w-auto sm:flex-1">
          <div className="text-sm font-semibold" data-testid="custom-chart-title">{title}</div>
          <div className="text-[10px] text-muted-foreground">{summary}</div>
        </div>
        <div className="flex gap-1 flex-wrap">
          {!donut && (
            <button type="button" onClick={() => setZoom(null)} disabled={!zoom}
                    className="btn-ghost px-2 py-1 text-[11px] inline-flex items-center gap-1 border hairline rounded disabled:opacity-40" data-testid="chart-reset-zoom">
              <MagnifyingGlassMinus size={11} /> Reset zoom
            </button>
          )}
          <button type="button" onClick={downloadPng} className="btn-ghost px-2 py-1 text-[11px] inline-flex items-center gap-1 border hairline rounded" data-testid="chart-download-png">
            <ImageSquare size={11} weight="duotone" /> PNG
          </button>
          <button type="button" onClick={downloadXlsx} className="btn-ghost px-2 py-1 text-[11px] inline-flex items-center gap-1 border hairline rounded" data-testid="chart-download-xlsx">
            <FileXls size={11} weight="duotone" /> Excel
          </button>
        </div>
      </div>
      <ChartLegend items={legend} hidden={hidden} onToggle={toggle} />
      {!donut && s.window === "full" && (
        <div className="text-[10px] text-muted-foreground -mt-1" data-testid="chart-past-future-note">
          Solid = paid till today · lighter / dotted = still to pay · tap a legend item to hide it
        </div>
      )}
      <div ref={box}>
        {empty ? (
          <div className="h-[200px] grid place-items-center text-xs text-muted-foreground" data-testid="custom-chart-empty">
            No data for this selection — try Full life or a wider date range.
          </div>
        ) : donut ? (
          <DonutChart data={donutRows} display={s.display} colors={colors} hidden={hidden} uid={uid} />
        ) : (
          <LoanChart data={visible} drawn={drawn} settings={s} hidden={hidden} colors={colors} mode={mode}
                     loanNames={built.loans} uid={uid} onZoom={onZoom}
                     markers={{ today: s.window === "full" ? built.today : null, crossover: built.crossover, events: built.events }} />
        )}
      </div>
      {!donut && !zoom && visible.length > 120 && BAR_TYPES.includes(s.chartType) && (
        <div className="text-[10px] text-muted-foreground" data-testid="chart-dense-hint">
          {visible.length} bars — drag the slider to zoom in, or pick Loan year / Financial year for a clearer view.
        </div>
      )}
      {!donut && !empty && (
        <RangeSlider max={values.length - 1} value={zoom || [0, values.length - 1]}
                     onChange={(v) => setZoom(v[0] === 0 && v[1] === values.length - 1 ? null : v)}
                     startLabel={visible[0]?.label} endLabel={visible[visible.length - 1]?.label} />
      )}
      <div className="flex flex-wrap items-center gap-3 border-t hairline pt-2 text-[11px]">
        <button type="button" onClick={() => setShowTable((v) => !v)} className="underline underline-offset-2 inline-flex items-center gap-1" data-testid="chart-show-table">
          <Table size={11} /> {showTable ? "Hide table" : "Show as table"}
        </button>
        <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid="chart-include-report-label">
          <input type="checkbox" checked={include} onChange={(e) => setInclude(e.target.checked)} data-testid="chart-include-report" />
          Include this chart in report
        </label>
        {!donut && <span className="text-muted-foreground text-[10px] hidden md:inline">Tip: drag across the chart to zoom.</span>}
      </div>
      {showTable && <ChartDataTable columns={columns} rows={tableRows} />}
    </div>
  );
}
